/** Ad/channel type display labels, keyed by advType string. */
export const ADV_TYPE_LABELS: Record<string, string> = {
  'pre-roll': 'YT Pre-roll',
  full: 'YT Full',
  short: 'YT Shorts',
  integration: 'YT Integration',
  'stream-highlight': 'YT Stream Highlight',
  'youtube-community-post': 'YT Community Post',
  stream: 'Stream',
  vod: 'Twitch VOD',
  tweet: 'Tweet',
  tiktok: 'TikTok',
  'tiktok-live': 'TikTok Live',
  douyin: 'Douyin',
  'instagram-post': 'Instagram Post',
  'instagram-reel': 'Instagram Reel',
  'instagram-story': 'Instagram Story',
  'facebook-post': 'Facebook Post',
  'facebook-reel': 'Facebook Reel',
  'discord-post': 'Discord Post',
  'footage-right': 'Footage Right',
  'chzzk-video': 'Chzzk Video',
  'afreeca-video': 'Afreeca Video',
  'bilibili-video': 'Bilibili Video',
  'sm-post': 'SM Post',
  'item-reimbursement': 'Item Reimb.',
};

export const ADV_TYPE_OPTIONS = Object.entries(ADV_TYPE_LABELS);

/**
 * Per-channel ordered advType preference used when adding a new rate row,
 * keyed by channel (platform) type. The first entry is the default selection;
 * subsequent entries are tried in order, skipping types already present.
 * Extends the original DEFAULT_ADV_TYPE_BY_CHANNEL_TYPE mapping with
 * stream-based platforms (chzzk/afreeca/bilibili) and facebook.
 */
export const ADV_TYPE_ORDER_BY_CHANNEL_TYPE: Record<string, string[]> = {
  youtube: ['pre-roll', 'full', 'short', 'integration', 'stream-highlight', 'youtube-community-post'],
  twitch: ['stream', 'vod'],
  chzzk: ['stream', 'chzzk-video'],
  afreeca: ['stream', 'afreeca-video'],
  bilibili: ['stream', 'bilibili-video'],
  tiktok: ['tiktok', 'tiktok-live'],
  // Instagram Reel is the default pick; Post and Story follow.
  instagram: ['instagram-reel', 'instagram-post', 'instagram-story'],
  douyin: ['douyin'],
  twitter: ['tweet'],
  facebook: ['facebook-post', 'facebook-reel'],
  discord: ['discord-post'],
};

/** Fallback advType for channel types without an explicit order. */
export const DEFAULT_ADV_TYPE = 'pre-roll';

/**
 * Ordered advType preference for a channel: the platform-specific order first,
 * then any remaining global types as a fallback so Add rate always resolves a
 * next type.
 */
export function getAdvTypePriority(channelType?: string | null): string[] {
  const order = (channelType && ADV_TYPE_ORDER_BY_CHANNEL_TYPE[channelType]) || [DEFAULT_ADV_TYPE];
  const rest = ADV_TYPE_OPTIONS.map(([k]) => k).filter((k) => !order.includes(k));
  return [...order, ...rest];
}

export interface VariationConfig {
  name: string;
  options: Array<{ value: number | string; text: string }>;
  default: number | string;
}

/** Ad types that carry a variation field (hours / duration). */
export const ADV_TYPES_WITH_VARIATION: Record<string, VariationConfig> = {
  stream: {
    name: 'hours',
    options: [1, 2, 3, 4].map((h) => ({ value: h, text: `${h}h` })),
    default: 2,
  },
  'footage-right': {
    name: 'duration',
    options: [
      { value: '1month', text: '1 month' },
      { value: '3months', text: '3 months' },
      { value: '6months', text: '6 months' },
      { value: '1year', text: '1 year' },
    ],
    default: '3months',
  },
  'tiktok-live': {
    name: 'hours',
    options: [1, 2, 3].map((h) => ({ value: h, text: `${h}h` })),
    default: 1,
  },
};

export interface RateEntry {
  type: string;
  price: string;
  variation?: number | string | null;
  updatedAt?: string | null;
}
